import React, { Suspense, useEffect } from 'react';

import { ErrorBoundary } from 'react-error-boundary';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { LinkEmbed } from '@atlassian/smart-knowledge-cards/link-embed';
import { LinkList } from '@atlassian/smart-knowledge-cards/link-list';
import { LoadingSkeleton as UserInlinePreviewListLoadingSkeleton } from '@atlassian/user-inline-preview/loading';
import { ErrorCard as UserPreviewErrorCard } from '@atlassian/user-profile-preview/error';
import { LoadingSkeleton as UserProfilePreviewLoadingSkeleton } from '@atlassian/user-profile-preview/loading';

import { useLuminaAnalyticsEvents } from '../../analytics/use-lumina-analytics-events';
import { ProjectHighlightsElement } from '../sub-agents/components/project-highlights/project-highlights';
import { RelatedPeopleElement } from '../sub-agents/components/related-people/related-people';
import type {
	ActionElementType,
	PeopleEntityElementType,
	PeopleEntityListElementType,
	SmartLinkElementType,
	SmartLinkListElementType,
} from '../types';
import { getUniquePeopleEntityAccountIdsFromList } from '../utils';

import { CarouselElement } from './carousel-element';
import { MarkdownElement } from './markdown-element';
import { PeopleEntityInlineListRelay } from './people-entity/people-entity-inline-list-relay';
import { PeopleEntityProfileRelay } from './people-entity/people-entity-profile-relay';
import { SmartLinkElement } from './smartlink-element';
import type { ElementRenderer, ElementTag } from './types';

const styles = cssMap({
	linkListWrapper: {
		marginTop: token('space.100'),
		marginBottom: token('space.100'),
		width: '100%',
	},
	linkEmbedWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
	peopleEntityWrapper: {
		marginTop: token('space.100'),
		width: '100%',
	},
});

/**
 * Adapter for smart link elements
 * Ensures all smart links are rendered as standalone cards.
 * When smartLinkRenderer is 'embed', renders a hero embed card instead.
 */
const SmartLinkElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
	smartLinkRenderer,
}) => {
	const url = (component as SmartLinkElementType).attributes?.url;
	if (smartLinkRenderer === 'embed' && url) {
		return (
			<Box xcss={styles.linkEmbedWrapper}>
				<LinkEmbed url={url} />
			</Box>
		);
	}

	return (
		<SmartLinkElement
			component={component as SmartLinkElementType}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for action card elements
 * Converts action card elements into smart link elements with validation
 */
const ActionCardElementAdapter: ElementRenderer = ({
	component,
	variant,
	isStreaming,
	sectionKey,
	containerType,
	indexInContainer,
}) => {
	const actionCard = component as ActionElementType;

	// Validate component has a url and convert to smart link
	if (!actionCard.attributes?.url) {
		return null;
	}

	const smartLinkComponent: SmartLinkElementType = {
		tag: 'smart-link',
		attributes: {
			url: actionCard.attributes.url,
			title: actionCard.attributes?.title,
			type: 'action',
			pos: actionCard.attributes?.pos,
		},
	};

	return (
		<SmartLinkElement
			component={smartLinkComponent}
			variant={variant}
			isStreaming={isStreaming}
			sectionKey={sectionKey}
			containerType={containerType}
			indexInContainer={indexInContainer}
		/>
	);
};

/**
 * Adapter for related people sub-agent elements
 * Extracts related people data from the sub-agent data map
 */
const RelatedPeopleElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<RelatedPeopleElement data={subAgentDataMap.relatedPeople} isStreaming={isStreaming} />
);

/**
 * Adapter for project highlights sub-agent elements
 * Extracts project highlights data from the sub-agent data map
 */
const ProjectHighlightsElementAdapter: ElementRenderer = ({ subAgentDataMap, isStreaming }) => (
	<ProjectHighlightsElement data={subAgentDataMap.projectHighlights} isStreaming={isStreaming} />
);

/**
 * Adapter for smart link list elements
 * Renders a vertical stack of smart link cards from item children
 */

const SmartLinkListElementAdapter: ElementRenderer = ({ component }) => {
	const listComponent = component as SmartLinkListElementType;
	const items = Array.isArray(listComponent.children) ? listComponent.children : [];
	const linkListUrls = items
		.map((item) => {
			if (typeof item === 'string') {
				return null;
			}
			const attrs = item.attributes as Record<string, string | undefined> | undefined;
			return attrs?.['url'];
		})
		.filter((item): item is string => !!item);

	return (
		<Box xcss={styles.linkListWrapper}>
			<LinkList urls={linkListUrls} />
		</Box>
	);
};

/**
 * ErrorBoundary fallback for the single-person UserProfilePreview.
 * Fires `operational.lumina.errored` (errorType: userProfilePreviewLoadFailed) when a
 * Relay or rendering error is caught, then renders the standard error card UI.
 */
function UserProfilePreviewErrorFallback(): React.JSX.Element {
	const { fireEvent } = useLuminaAnalyticsEvents();
	useEffect(() => {
		fireEvent('operational.lumina.errored', {
			errorType: 'userProfilePreviewLoadFailed',
			context: null,
		});
		// fireEvent is stable — safe to omit from deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return <UserPreviewErrorCard />;
}

/**
 * ErrorBoundary fallback for the multi-person UserInlinePreviewList.
 * Fires `operational.lumina.errored` (errorType: userInlinePreviewListLoadFailed) when a
 * Relay or rendering error is caught, then renders the standard error card UI.
 */
function UserInlinePreviewListErrorFallback(): React.JSX.Element {
	const { fireEvent } = useLuminaAnalyticsEvents();
	useEffect(() => {
		fireEvent('operational.lumina.errored', {
			errorType: 'userInlinePreviewListLoadFailed',
			context: null,
		});
		// fireEvent is stable — safe to omit from deps
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return <UserPreviewErrorCard />;
}

/**
 * Single people entity — Relay profile preview
 */
const PeopleEntityElementAdapter: ElementRenderer = ({ component, ...props }) => {
	const accountId = (component as PeopleEntityElementType).attributes?.accountId;
	if (!accountId) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<ErrorBoundary fallbackRender={() => <UserProfilePreviewErrorFallback />}>
				<Suspense fallback={<UserProfilePreviewLoadingSkeleton />}>
					<PeopleEntityProfileRelay accountId={accountId} {...props} />
				</Suspense>
			</ErrorBoundary>
		</Box>
	);
};

/**
 * List of people entities — one Relay `userSearchV2` for all child account IDs.
 */
const PeopleEntityListElementAdapter: ElementRenderer = ({ component, source, ...props }) => {
	const listComponent = component as PeopleEntityListElementType;
	const accountIds = getUniquePeopleEntityAccountIdsFromList(listComponent);

	const inSAINFeature = source === 'luminaSAIN';
	const columnCount = inSAINFeature ? 2 : 1;
	const itemCount = inSAINFeature ? 8 : 4;

	// The list tag has been seen in the stream but is not yet closed — show the
	// skeleton immediately so there's no blank gap while parsing completes.
	if (listComponent.attributes?.loading === 'true') {
		return (
			<Box xcss={styles.peopleEntityWrapper}>
				<UserInlinePreviewListLoadingSkeleton columnCount={columnCount} itemCount={itemCount} />
			</Box>
		);
	}

	if (!accountIds.length) {
		return null;
	}

	return (
		<Box xcss={styles.peopleEntityWrapper}>
			<ErrorBoundary fallbackRender={() => <UserInlinePreviewListErrorFallback />}>
				<Suspense
					fallback={
						<UserInlinePreviewListLoadingSkeleton columnCount={columnCount} itemCount={itemCount} />
					}
				>
					<PeopleEntityInlineListRelay
						accountIds={accountIds}
						cloudId={props.cloudId}
						orgId={props.orgId}
						requestingUserId={props.requestingUserId}
						testId="lumina-people-entity-list"
						initialVisibleCount={itemCount}
						layout={inSAINFeature ? 'grid' : 'stack'}
					/>
				</Suspense>
			</ErrorBoundary>
		</Box>
	);
};

/**
 * Centralized map of element renderers
 * Maps element tags to their corresponding component references
 *
 * Components with direct references (MarkdownElement, CarouselElement) require no adaptation.
 * Components with adapters handle prop mapping/data transformation.
 */
export const elementRenderers: Record<ElementTag, ElementRenderer> = {
	'action-card': ActionCardElementAdapter,
	carousel: CarouselElement as ElementRenderer,
	md: MarkdownElement as ElementRenderer,
	'people-entity': PeopleEntityElementAdapter,
	'people-entity-list': PeopleEntityListElementAdapter,
	'project-highlights': ProjectHighlightsElementAdapter,
	'related-people': RelatedPeopleElementAdapter,
	'smart-link': SmartLinkElementAdapter,
	'smart-link-list': SmartLinkListElementAdapter,
};
